/**
 * notificationRouter.js
 *
 * Dynamic action-oriented notification router for Web App based on user role and notification type.
 * Mirrors the mobile app (RichNaturalNew) router logic to ensure consistent deep-linking across roles.
 */

// Buyer Route Map
const BUYER_ROUTE_MAP = {
  WALLET_DEBIT_SUCCESS: "WALLET",
  BUYER_FIRST_ORDER_BONUS: "WALLET",
  ORDER_CREATED: "ORDER_DETAILS",
  ORDER_REVIEW_APPROVED: "ORDER_DETAILS",
  ORDER_REVIEW_REJECTED: "ORDER_DETAILS",

  QUOTATION_RECEIVED: "QUOTATION_LIST",

  TRANSPORT_CHARGE_REQUESTED: "QUOTATION_DETAILS",
  TRANSPORT_CHARGE_FULL_ORDER_AVAILABLE: "QUOTATION_DETAILS",
  TRANSPORT_CHARGE_FULL_ORDER_UPDATED: "QUOTATION_DETAILS",
  TRANSPORT_CHARGE_SAMPLE_AVAILABLE: "CART",
  TRANSPORT_CHARGE_SAMPLE_UPDATED: "CART",
  PROFORMA_INVOICE_REQUESTED: "QUOTATION_DETAILS",
  ORIGINAL_INVOICE_UPLOADED: "QUOTATION_DETAILS",
  SAMPLE_APPROVAL_UPDATED: "QUOTATION_DETAILS",
  OFM_VISIT_PASS_GENERATED: "QUOTATION_DETAILS",
  MANUAL_VISIT_PASS_PARTNER_ASSIGNED: "GATE_PASSES",
  OFM_PAYOUT_ADDED: "QUOTATION_DETAILS",
  OFM_PAYOUT_UPDATED: "QUOTATION_DETAILS",
  LC_DOCUMENT_UPLOADED: "QUOTATION_DETAILS",
  LC_DOCUMENT_REVIEWED: "QUOTATION_DETAILS",
  BANK_TRANSFER_PROOF_UPLOADED: "QUOTATION_DETAILS",
  BANK_TRANSFER_VERIFIED: "QUOTATION_DETAILS",
  ORDER_DISPATCHED: "QUOTATION_DETAILS",
  TRACKING_DETAILS_UPDATED: "QUOTATION_DETAILS",
  ORDER_DELIVERED: "QUOTATION_DETAILS",
  FULL_ORDER_SATISFIED: "QUOTATION_DETAILS",

  DISPUTE_RAISED: "RETURN_DETAILS",
  DISPUTE_VERIFICATION_STARTED: "RETURN_DETAILS",
  DISPUTE_REJECTED: "RETURN_DETAILS",
  RETURN_REQUEST_APPROVED: "RETURN_DETAILS",
  RETURN_REQUEST_REJECTED: "RETURN_DETAILS",
  RETURN_DISPATCHED: "RETURN_DETAILS",
  RETURN_TRACKING_UPDATED: "RETURN_DETAILS",
  RETURN_DELIVERED: "RETURN_DETAILS",
  REFUND_PROCESSED: "RETURN_DETAILS",
  REFUND_RECEIVED_CONFIRMATION: "RETURN_DETAILS",
  CRM_ASSIGNED: "ACCOUNT",
};

// Supplier Route Map
const SUPPLIER_ROUTE_MAP = {
  NEW_ORDER: "ORDER_DETAILS",
  WALLET_DEBIT_SUCCESS: "WALLET",
  QUOTATION_SELECTION: "QUOTATION_DETAILS",
  QUOTATION_SELECTED: "QUOTATION_DETAILS",
  QUOTATION_REVIEW_UPDATED: "QUOTATION_DETAILS",
  FIRST_QUOTATION_BONUS: "WALLET",
  TRANSPORT_CHARGE_REQUESTED: "QUOTATION_DETAILS",
  PROFORMA_INVOICE_REQUESTED: "QUOTATION_DETAILS",
  SUPPLIER_PROFORMA_UPLOADED: "QUOTATION_DETAILS",
  SUPPLIER_SAMPLE_PROFORMA_UPLOADED: "QUOTATION_DETAILS",
  OFM_ASSIGNMENT_ACCEPTED: "QUOTATION_DETAILS",
  OFM_IMPRESSION_ADDED: "QUOTATION_DETAILS",
  OFM_VISIT_PASS_GENERATED: "QUOTATION_DETAILS",
  MANUAL_VISIT_PASS_PARTNER_ASSIGNED: "GATE_PASSES",
  SUPPLIER_GST_INVOICE_UPLOADED: "QUOTATION_DETAILS",
  SUPPLIER_SAMPLE_GST_INVOICE_UPLOADED: "QUOTATION_DETAILS",
  TRACKING_DETAILS_UPDATED: "QUOTATION_DETAILS",
  SAMPLE_APPROVAL_UPDATED: "QUOTATION_DETAILS",
  ORIGINAL_INVOICE_UPLOADED: "QUOTATION_DETAILS",
  SUPPLIER_PAYMENT_CONFIRMED: "QUOTATION_DETAILS",
  REFUND_RECEIVED_CONFIRMATION: "QUOTATION_DETAILS",
  BANK_TRANSFER_VERIFIED: "QUOTATION_DETAILS",
  ORDER_DISPATCHED: "QUOTATION_DETAILS",
  ORDER_DELIVERED: "QUOTATION_DETAILS",
  SUPPLIER_LC_DOCUMENT_SHARED: "QUOTATION_DETAILS",
  QUOTATION_CONVERTED_TO_OFFER: "QUOTATION_DETAILS",
  CASH_DISCOUNT_ADVANCE_REQUESTED: "QUOTATION_DETAILS",
  CASH_DISCOUNT_PAYMENT_PROOF_UPLOADED: "QUOTATION_DETAILS",

  DISPUTE_RAISED: "RETURN_DETAILS",
  DISPUTE_VERIFICATION_STARTED: "RETURN_DETAILS",
  DISPUTE_REJECTED: "RETURN_DETAILS",
  RETURN_REQUEST_APPROVED: "RETURN_DETAILS",
  RETURN_REQUEST_REJECTED: "RETURN_DETAILS",
  RETURN_DISPATCHED: "RETURN_DETAILS",
  RETURN_TRACKING_UPDATED: "RETURN_DETAILS",
  RETURN_DELIVERED: "RETURN_DETAILS",
  CRM_ASSIGNED: "ACCOUNT",
  RQM_APPROVED: "RQM_REQUESTS",
  RQM_REJECTED: "RQM_REQUESTS",
  VARIETY_LINKED: "VARIETY_LINKED",
};

// OFM Route Map
const OFM_ROUTE_MAP = {
  OFM_ASSIGNMENT_REQUESTED: "ASSIGNMENTS",
  OFM_ASSIGNMENT_ACCEPTED: "ASSIGNMENTS",
  OFM_IMPRESSION_ADDED: "ASSIGNMENTS",
  OFM_VISIT_PASS_GENERATED: "ASSIGNMENTS",
  MANUAL_VISIT_PASS_PARTNER_ASSIGNED: "ASSIGNMENTS",
  OFM_PAYOUT_ADDED: "ASSIGNMENTS",
  OFM_PAYOUT_UPDATED: "ASSIGNMENTS",
  MANUAL_OFM_PAYOUT_CREDITED: "ASSIGNMENTS",

  DISPUTE_RAISED: "DISPUTES",
  DISPUTE_VERIFICATION_STARTED: "DISPUTES",
  DISPUTE_REJECTED: "DISPUTES",
};

const ROLE_MAPS = {
  buyer: BUYER_ROUTE_MAP,
  supplier: SUPPLIER_ROUTE_MAP,
  ofm: OFM_ROUTE_MAP,
};

/**
 * Handles navigation when a notification is clicked based on user role and notification type.
 *
 * @param {Object} notification - Notification payload object
 * @param {Function} navigate - React Router navigate hook
 * @param {string} [userRole] - Logged in user's role ('buyer', 'supplier', etc.)
 */
export const handleNotificationNavigation = (
  notification,
  navigate,
  userRole = "",
) => {
  if (!navigate || !notification) return;

  const role = (userRole || "buyer").toLowerCase().trim();
  const type = notification.type || notification.data?.type;

  const orderId =
    (typeof notification.orderId === "object"
      ? notification.orderId?._id
      : notification.orderId) ||
    notification.data?.orderId?._id ||
    notification.data?.orderId ||
    notification.metadata?.orderId;
  let orderNumber =
    notification.orderNumber ||
    (typeof notification.orderId === "object"
      ? notification.orderId?.orderNumber
      : null) ||
    notification.data?.orderNumber ||
    notification.metadata?.orderNumber;
  const quotationId =
    (typeof notification.quotationId === "object"
      ? notification.quotationId?._id
      : notification.quotationId) ||
    notification.data?.quotationId ||
    notification.metadata?.quotationId;

  // Extract orderNumber from notification message if not directly present
  if (!orderNumber) {
    const rawMsg =
      notification.message ||
      notification.data?.message ||
      notification.metadata?.message ||
      "";
    const match = rawMsg.match(/order\s+([A-Za-z0-9_-]+)/i);
    if (match) {
      orderNumber = match[1];
    }
  }

  const roleMap =
    ROLE_MAPS[role] ||
    (role === "supplier" ? SUPPLIER_ROUTE_MAP : BUYER_ROUTE_MAP);
  const action = roleMap[type] || "ORDER_DETAILS";

  console.log(
    `[NotificationRouter Web] Role: ${role}, Type: ${type}, Action: ${action}, OrderId: ${orderId}, OrderNumber: ${orderNumber}, QuotationId: ${quotationId}`,
  );

  switch (role) {
    case "buyer": {
      const title = (
        notification.title ||
        notification.data?.title ||
        notification.metadata?.title ||
        ""
      ).toLowerCase();
      const message = (
        notification.message ||
        notification.data?.message ||
        notification.data?.body ||
        notification.metadata?.message ||
        ""
      ).toLowerCase();

      // Specifically handle RETURN_REQUEST_APPROVED for buyer:
      // Directly navigates to the Returns section with orderNumber, highlight, and scrolls to Return Dispatch
      const isReturnApproved =
        type === "RETURN_REQUEST_APPROVED" ||
        (title.includes("return") && title.includes("approved")) ||
        (message.includes("return") && message.includes("approved"));

      if (isReturnApproved) {
        const queryParams = new URLSearchParams();
        if (orderNumber) queryParams.set("orderNumber", orderNumber);
        if (orderId) queryParams.set("highlight", orderId);
        else if (orderNumber) queryParams.set("highlight", orderNumber);
        queryParams.set("section", "return-dispatch");

        return navigate(`/returns?${queryParams.toString()}`, {
          state: {
            scrollTo: "return-dispatch",
            highlightSection: true,
            orderNumber: orderNumber,
            orderId: orderId,
          },
        });
      }

      const isDisputeType =
        type?.includes("DISPUTE") ||
        type?.includes("INVESTIGATION") ||
        notification.passType === "Dispute" ||
        notification.data?.passType === "Dispute" ||
        notification.metadata?.passType === "Dispute" ||
        notification.stageType === "Dispute" ||
        notification.data?.stageType === "Dispute" ||
        notification.metadata?.stageType === "Dispute" ||
        title.includes("dispute") ||
        message.includes("dispute");

      if (isDisputeType) {
        const queryParams = new URLSearchParams();
        if (orderNumber) queryParams.set("orderNumber", orderNumber);
        if (orderId) queryParams.set("highlight", orderId);
        else if (orderNumber) queryParams.set("highlight", orderNumber);
        const qs = queryParams.toString();
        return navigate(qs ? `/returns?${qs}` : "/returns", {
          state: {
            orderNumber,
            orderId,
          },
        });
      }

      switch (action) {
        case "ACCOUNT":
          return navigate("/account");
        case "GATE_PASSES":
          return navigate("/gate-passes");
        case "WALLET":
          return navigate("/wallet");
        case "QUOTATION_LIST":
        case "QUOTATION_DETAILS":
        case "CART":
          if (!orderId && !quotationId) {
            return navigate("/gate-passes");
          }
          return navigate(
            orderId ? `/quotations?highlight=${orderId}` : "/quotations",
          );
        case "RETURN_DETAILS": {
          const queryParams = new URLSearchParams();
          if (orderNumber) queryParams.set("orderNumber", orderNumber);
          if (orderId) queryParams.set("highlight", orderId);
          else if (orderNumber) queryParams.set("highlight", orderNumber);
          const qs = queryParams.toString();
          return navigate(qs ? `/returns?${qs}` : "/returns", {
            state: {
              orderNumber,
              orderId,
            },
          });
        }
        case "ORDER_DETAILS":
        default:
          if (!orderId && !orderNumber) return;
          return navigate(
            orderId
              ? `/orders?highlight=${orderId}`
              : `/orders?orderNumber=${orderNumber}`,
          );
      }
    }

    case "supplier": {
      switch (action) {
        case "ACCOUNT":
          return navigate("/account");
        case "GATE_PASSES":
          return navigate("/gate-passes");
        case "WALLET":
          return navigate("/wallet");
        case "QUOTATION_DETAILS":
          if (quotationId) {
            return navigate(
              `/supplier-quotation-details?quotationId=${quotationId}${orderId ? `&orderId=${orderId}` : ""
              }`,
            );
          }
          if (orderId) {
            return navigate(`/supplier-quotations?orderId=${orderId}`);
          }
          return navigate("/gate-passes");
        case "RETURN_DETAILS":
          return navigate(
            orderId
              ? `/supplier-returned-orders?highlight=${orderId}`
              : "/supplier-returned-orders",
          );
        case "RQM_REQUESTS":
          return navigate("/supplier-rqm-requests");
        case "VARIETY_LINKED":
          return navigate("/supplier-linked-varieties");
        case "PENALTY_DETAILS":
        case "PENALTIES":
          return navigate("/supplier-penalties");
        case "ORDER_DETAILS":
        default:
          return navigate(
            orderId
              ? `/supplier-orders?highlight=${orderId}`
              : "/supplier-orders",
          );
      }
    }

    case "ofm": {
      switch (action) {
        case "ACCOUNT":
          return navigate("/account");
        case "ASSIGNMENTS":
          return navigate(orderId ? `/orders?highlight=${orderId}` : "/orders");
        case "DISPUTES":
          return navigate(
            orderId ? `/returns?highlight=${orderId}` : "/returns",
          );
        case "ORDER_DETAILS":
        default:
          return navigate(orderId ? `/orders?highlight=${orderId}` : "/orders");
      }
    }

    default: {
      if (role === "supplier") {
        return navigate(
          orderId
            ? `/supplier-orders?highlight=${orderId}`
            : "/supplier-orders",
        );
      }
      return navigate(orderId ? `/orders?highlight=${orderId}` : "/orders");
    }
  }
};
